<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TemplatePreview',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    sampleData: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const beginRenderTemplate = (...args) => {
    const item = props.item;

    isPDFLoading.value = true;
    setData('previewError', null);
    setData('file', null);

    let parsedData;
    try {
      parsedData = JSON.parse(props.sampleData);
    } catch (e) {
      isPDFLoading.value = false;
      setData('previewError', e.message);
      return null;
    }

    $app.value.connections
      .request('render_template_test_html', {
        content: {
          ...props.item.getData(),
          data: parsedData,
        },
        responseType: 'arraybuffer',
      })
      .then((response) => {
        console.log(response);
        console.log(response.data);

        setData(
          'file',
          new File([response.data], 'result.pdf', {
            type: response.headers['content-type'],
          })
        );
      })
      .catch((err) => {
        console.error(err);
        setData('previewError', err.message || String(err));
      })
      .finally(() => (isPDFLoading.value = false));
  };

  const isPDFLoading = ref(true);
  const previewData = ref(null);
  const file = ref(null);
  const previewError = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'full-screen',
  }));

  const $data = computed(() => ({
    isPDFLoading: isPDFLoading.value,
    previewData: previewData.value,
    file: file.value,
    previewError: previewError.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_previewData = async () => {
    return beginRenderTemplate();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isPDFLoading' },
      { name: 'previewData' },
      { name: 'file' },
      { name: 'previewError' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { previewData: $$load_previewData },
    ux: {},
    dataRefs: {
      isPDFLoading,
      previewData,
      file,
      previewError,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('TemplatePreview', 'Preview') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.sampleData,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('sampleData');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0i4h2asaq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_0i4h2asaq =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0i4h2asaq_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dooh090fc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0i4h2asaq,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dooh090fc],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_de1y52tkj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_udbd2lf41 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_de1y52tkj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_udbd2lf41],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bawpoikv8 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_hbcr5y5p5 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bawpoikv8,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_hbcr5y5p5],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_wt0yy10qf =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop_qkmy9gkuk =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_wt0yy10qf,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_clickPreventStop_qkmy9gkuk],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeModal_iks90fi9z =
    new actionTypes.CloseModalAction({});
  const inst_evh_2140198529698299_clickPreventStop_am9y09xrd =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_iks90fi9z,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_am9y09xrd],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_dooh090fc,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_udbd2lf41,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_hbcr5y5p5,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_clickPreventStop_qkmy9gkuk,
    reload: inst_evh_6466705138206980_clickPreventStop_qkmy9gkuk,
    inst_evh_6466705138206980_clickPreventStop,
    inst_evh_2140198529698299_clickPreventStop_am9y09xrd,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_dooh090fc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dooh090fc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_udbd2lf41 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_udbd2lf41.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_hbcr5y5p5 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_hbcr5y5p5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop_qkmy9gkuk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop_qkmy9gkuk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_am9y09xrd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_am9y09xrd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="TemplatePreview"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <ItpButton
          variant="default"
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          :spinning="actions.reload.isRunning || isPDFLoading"
          class="mx-3"
          @click.prevent.stop="
            evh_6466705138206980_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <div v-if="!file && isPDFLoading">Loading...</div>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" class="h-100" v-if="loaded">
      <ItpPdfViewer :file="file" class="h-100" v-if="file"> </ItpPdfViewer>
      <div class="h-100 d-flex" v-if="previewError">
        <BAlert show variant="danger" class="m-auto">
          {{ previewError }}
        </BAlert>
      </div>
      <div class="h-100 d-flex" v-if="!sampleData">
        <BAlert show variant="primary" class="m-auto">
          {{
            pgettext(
              'Templates',
              'Generate HTML previews using the data loaded/entered at the Data tab.'
            )
          }}
        </BAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Templates', 'Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
